import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Icon, Select } from "semantic-ui-react";
import { DataLayerOptions } from ".";
import { dataLayersDropdownQueryOptions } from "../../../../../apis/dataLayerApi";
import { UUID } from "../../../../../apis/types/dataLayerTypes";
import { useAuth0 } from "../../../../../auth/auth0";
import { useModuleNavigation } from "../../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../../hooks/useUserAuth";
import { useDataDispatch, useGraphDispatch, useViewerDispatch } from "../StateManagement";
import { GEOMFA_MENU_ZONE } from "../../../../../utilities/defaults";
import _ from "lodash";

type Props = {
	minimized: boolean;
};

export const DataLayerDropdown = ({ minimized }: Props) => {
	const {
		updateDataLayerGeometryUrl,
		updatePlotAssociatedGeometryUrl,
		updateDataLayerGroupMap,
		updateDataLayerVisible,
	} = useViewerDispatch();
	const { updateDataLayerNames } = useDataDispatch();
	const { updateAxis } = useGraphDispatch();
	const moduleNavigation = useModuleNavigation();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();

	const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } = useSortable({ id: "data" });

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	const [userSelectedId, setUserSelectedId] = useState<UUID | undefined>();
	const [dataLayerVisible, setDataLayerVisible] = useState(true);
	const dataLayerDropdownQueryOpts = dataLayersDropdownQueryOptions(
		getTokenSilently,
		userAuth.currentClientId!,
		moduleNavigation.trialId!,
	);

	const { data: dropdownInfo, isFetching } = useQuery(dataLayerDropdownQueryOpts);

	const defaultId =
		!isFetching && dropdownInfo?.length > 0
			? (GEOMFA_MENU_ZONE.reduce<UUID | undefined>(
					(acc, name) => acc ?? dropdownInfo.find((dl) => dl.factorName.includes(name))?.factorId,
					undefined,
				) ?? dropdownInfo[0].factorId)
			: "";
	const selectedDataLayerId = userSelectedId ?? defaultId;

	const dataLayerOptions = useMemo(() => {
		if (dropdownInfo.length > 0) {
			return _.orderBy(dropdownInfo, "factorName").map((dl) => ({
				text: dl.factorName,
				value: dl.factorId,
				key: dl.factorId,
			}));
		} else {
			return [];
		}
	}, [dropdownInfo]);

	useEffect(() => {
		if (dropdownInfo.length > 0 && selectedDataLayerId) {
			if (dataLayerVisible) {
				const layer = dropdownInfo.find((dl) => dl.factorId === selectedDataLayerId);
				if (layer) {
					updateDataLayerGeometryUrl(layer.dataLayerUploadGeometrySasUrl);
					updatePlotAssociatedGeometryUrl(layer.plotAssociatedGeometrySasUrl);
					updateDataLayerGroupMap(layer.plotMap);
					updateAxis({ x: layer.factorName });
				}
			} else {
				updateDataLayerGeometryUrl(undefined);
				updatePlotAssociatedGeometryUrl(undefined);
				updateDataLayerGroupMap({});
				updateDataLayerNames([]);
				updateAxis({ x: "All Plots", xOrder: ["None"] });
			}
		}
	}, [
		selectedDataLayerId,
		dropdownInfo,
		dataLayerVisible,
		updateDataLayerGeometryUrl,
		updatePlotAssociatedGeometryUrl,
		updateDataLayerGroupMap,
		updateAxis,
		updateDataLayerNames,
	]);

	return (
		<div ref={setNodeRef} className="layerBlock" style={style} {...attributes}>
			<div ref={setActivatorNodeRef} className="layerBlockBar" {...listeners}>
				<Icon className="drag-handle-vertical" />
			</div>
			<Icon className="layerBlockIcon data layers" size="large" />
			<span className="layerBlockLabel">
				{minimized ? (dataLayerOptions.find((o) => o.value === selectedDataLayerId)?.text ?? "No Zone Data") : "Zones"}
			</span>
			{dataLayerOptions.length > 0 || isFetching ? (
				<>
					<div className="layerBlockButtons">
						<Button
							className="actionsPopupTrigger iconButton"
							icon
							onClick={() => {
								updateDataLayerVisible(!dataLayerVisible);
								setDataLayerVisible((prev) => !prev);
							}}
						>
							<Icon className={`eye${dataLayerVisible ? "" : " closed"}`} size="large" />
						</Button>
						<DataLayerOptions />
					</div>
					<Select
						className="layerBlockSelect"
						fluid
						loading={isFetching}
						options={dataLayerOptions}
						value={selectedDataLayerId}
						onChange={(_, data) => {
							setUserSelectedId(data.value as string);
						}}
					/>
				</>
			) : (
				<>
					<div className="layerBlockButtons">
						<Icon className={"warning"} size="large" style={{ color: "var(--gold-500)" }} />
					</div>
					<Select
						className="layerBlockSelect"
						fluid
						disabled
						options={dataLayerOptions}
						placeholder={"No Zonal Data Layers Found!"}
					/>
				</>
			)}
		</div>
	);
};
